<template>
  <div class="card" v-if="sepa">
    <div class="card-header d-flex align-items-center justify-content-between gap-2">
      <div>
        <span>{{ $t('invoices.sepa_direct_debit.title') }}</span>
        <span v-if="sepa.preview" class="badge bg-warning text-dark ms-2">
          {{ $t('invoices.sepa_direct_debit.preview') }}
        </span>
      </div>
      <div class="d-flex gap-2">
        <button
          v-if="canSetupDirectDebitBooking"
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="settingUpDirectDebitBooking"
          @click="setupDirectDebitBooking"
        >
          <i class="fa-fw fa-solid fa-money-check-dollar"></i>
          {{ $t('invoices.sepa_direct_debit.setup_direct_debit_booking') }}
        </button>
        <button
          v-if="canRetryDirectDebit"
          type="button"
          class="btn btn-sm btn-outline-primary"
          :disabled="retryingDirectDebit"
          @click="retryDirectDebit"
        >
          <i class="fa-fw fa-solid fa-rotate-right"></i>
          {{ $t('invoices.sepa_direct_debit.retry_direct_debit') }}
        </button>
      </div>
    </div>
    <div class="card-body">
      <div class="alert alert-info py-2 mb-3" v-if="sepa.preview">
        {{ $t('invoices.sepa_direct_debit.preview_hint') }}
      </div>

      <div class="row g-3">
        <div class="col-6 col-xl-2">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.invoice_date') }}</div>
          <div>{{ formatDate(invoice.invoice_date, 'short2') || '-' }}</div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.amount') }}</div>
          <div>{{ formatMoney(sepa.amount, sepa.currency) }}</div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.requested_on') }}</div>
          <div>{{ formatDate(sepa.requested_on, 'short2') || '-' }}</div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.sequence_type') }}</div>
          <div>{{ sepa.sequence_type || '-' }}</div>
        </div>
        <div class="col-6 col-xl-3">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.mandate') }}</div>
          <div>{{ mandateLabel }}</div>
        </div>
        <div class="col-6 col-xl-3">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.mandate_iban') }}</div>
          <div>{{ sepa.mandate_iban || '-' }}</div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.active_export') }}</div>
          <router-link v-if="sepa.active_export_id" :to="{name: ROUTES.DUNNING.SEPA_DIRECT_DEBIT_EXPORT, params: {id: sepa.active_export_id}}">
            {{ exportLabel(sepa.active_export_id) }}
          </router-link>
          <div v-else>-</div>
        </div>
        <div class="col-6 col-xl-2">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.booking_state') }}</div>
          <div>{{ sepa.booking_state || '-' }}</div>
        </div>
        <div class="col-12">
          <div class="text-muted small">{{ $t('invoices.sepa_direct_debit.remittance_information') }}</div>
          <div>{{ sepa.remittance_information || '-' }}</div>
        </div>
      </div>

      <div class="alert alert-warning mt-3 mb-0" v-if="blockingReasons.length">
        <div class="fw-bold mb-2">{{ $t('invoices.sepa_direct_debit.blocking_reasons') }}</div>
        <ul class="mb-0">
          <li v-for="reason in blockingReasons" :key="reason">{{ reasonLabel(reason) }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import {Helpers} from "@/helpers";
import {ROUTES} from "@/scripts/constants";
import {useInvoiceStore} from "@/stores/invoice";

export default {
  name: 'SepaDirectDebitCard',
  emits: ['changed'],
  props: {
    invoice: {
      type: Object,
      required: true,
    },
  },
  setup() {
    return {
      invoiceStore: useInvoiceStore(),
      ROUTES,
    }
  },
  data() {
    return {
      retryingDirectDebit: false,
      settingUpDirectDebitBooking: false,
    }
  },
  computed: {
    sepa() {
      return this.invoice?.sepa_direct_debit || null
    },
    blockingReasons() {
      return this.sepa?.blocking_reasons || []
    },
    canRetryDirectDebit() {
      return !!this.invoice?.id && !!this.sepa && !this.sepa.preview
    },
    canSetupDirectDebitBooking() {
      return !!this.invoice?.id && !!this.sepa?.preview && !!this.sepa?.eligible
    },
    mandateLabel() {
      if (!this.sepa?.direct_debit_mandate_id && !this.sepa?.mandate_reference) {
        return '-'
      }
      return [
        this.sepa.direct_debit_mandate_id ? `#${this.sepa.direct_debit_mandate_id}` : null,
        this.sepa.mandate_reference,
      ].filter(Boolean).join(' · ')
    },
  },
  methods: {
    setupDirectDebitBooking() {
      if (!window.confirm(this.$t('invoices.sepa_direct_debit.setup_direct_debit_booking_confirm', {
        mandate: this.mandateLabel,
        iban: this.sepa.mandate_iban || '-',
      }))) {
        return
      }

      this.settingUpDirectDebitBooking = true
      this.invoiceStore.setupDirectDebitBooking(this.invoice.id).then(({data}) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'success',
          summary: data.message || this.$t('invoices.sepa_direct_debit.setup_direct_debit_booking_success'),
        })
        this.$emit('changed')
      }).catch((error) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'error',
          summary: error.response?.data?.message || this.$t('invoices.sepa_direct_debit.setup_direct_debit_booking_failed'),
        })
      }).finally(() => {
        this.settingUpDirectDebitBooking = false
      })
    },
    retryDirectDebit() {
      if (!window.confirm(this.$t('invoices.sepa_direct_debit.retry_direct_debit_confirm'))) {
        return
      }

      this.retryingDirectDebit = true
      this.invoiceStore.retryDirectDebit(this.invoice.id).then(({data}) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'success',
          summary: data.message || this.$t('invoices.sepa_direct_debit.retry_direct_debit_success'),
        })
        this.$emit('changed')
      }).catch((error) => {
        Helpers.emitter.emit('show-toast', {
          severity: 'error',
          summary: error.response?.data?.message || this.$t('invoices.sepa_direct_debit.retry_direct_debit_failed'),
        })
      }).finally(() => {
        this.retryingDirectDebit = false
      })
    },
    formatMoney(amount, currency) {
      if (amount === null || amount === undefined) {
        return '-'
      }
      return new Intl.NumberFormat(this.$i18n.locale, {style: 'currency', currency: currency || 'EUR'}).format(Number(amount))
    },
    reasonLabel(reason) {
      const key = `invoices.sepa_direct_debit.reasons.${reason}`
      const translation = this.$t(key)
      return translation === key ? reason : translation
    },
    exportLabel(exportId) {
      return this.$t('sepa_direct_debits.export_tab', {id: exportId})
    },
  },
}
</script>
